// Node detail panel - shows full information when a node is clicked - 145 lines ✅
import { KGNode } from '@/hooks/useKnowledgeGraph'
import { X, Calendar, Tag, Database, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { format } from 'date-fns'

interface NodeDetailPanelProps {
  node: KGNode | null
  onClose: () => void
}

export function NodeDetailPanel({ node, onClose }: NodeDetailPanelProps) {
  if (!node) {
    return null
  }

  const properties = node.properties as Record<string, unknown>

  // Entity type badge color
  const entityTypeBadgeClass = {
    issue: 'bg-red-500 text-white',
    knowledge_entry: 'bg-teal-500 text-white',
    resolution_method: 'bg-green-500 text-white',
  }[node.entity_type] || 'bg-gray-500 text-white'

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border border-kaycoo-teal-dark bg-white shadow-lg">
      {/* Header */}
      <div className="flex items-start justify-between border-b border-kaycoo-teal-dark bg-kaycoo-teal-dark p-4">
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-white">{node.label}</h2>
          <Badge className={`mt-2 ${entityTypeBadgeClass}`}>
            {node.entity_type.replace(/_/g, ' ').toUpperCase()}
          </Badge>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="text-white hover:bg-kaycoo-teal-dark-2"
        >
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Content */}
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        {/* Timestamps */}
        <div className="space-y-2">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-kaycoo-teal-dark">
            <Calendar className="h-4 w-4" />
            Timestamps
          </h3>
          <div className="space-y-1 text-sm text-kaycoo-charcoal-2">
            <div className="flex justify-between">
              <span>Created:</span>
              <span>{format(new Date(node.created_at), 'PPp')}</span>
            </div>
            <div className="flex justify-between">
              <span>Updated:</span>
              <span>{format(new Date(node.updated_at), 'PPp')}</span>
            </div>
          </div>
        </div>

        {/* D365 Information (if available) */}
        {Boolean(properties.d365_version || properties.d365_versions || properties.d365_module || properties.d365_modules) && (
          <div className="space-y-2">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-kaycoo-teal-dark">
              <Tag className="h-4 w-4" />
              D365 Information
            </h3>
            <div className="space-y-1 text-sm text-kaycoo-charcoal-2">
              {Boolean(properties.d365_version) && (
                <div className="flex justify-between">
                  <span>Version:</span>
                  <Badge variant="outline">{String(properties.d365_version)}</Badge>
                </div>
              )}
              {Boolean(properties.d365_versions) && Array.isArray(properties.d365_versions) && (
                <div className="flex flex-wrap gap-1">
                  <span>Versions:</span>
                  {(properties.d365_versions as string[]).map((v, i) => (
                    <Badge key={i} variant="outline">{v}</Badge>
                  ))}
                </div>
              )}
              {Boolean(properties.d365_module) && (
                <div className="flex justify-between">
                  <span>Module:</span>
                  <Badge variant="outline">{String(properties.d365_module)}</Badge>
                </div>
              )}
              {Boolean(properties.d365_modules) && Array.isArray(properties.d365_modules) && (
                <div className="flex flex-wrap gap-1">
                  <span>Modules:</span>
                  {(properties.d365_modules as string[]).map((m, i) => (
                    <Badge key={i} variant="outline">{m}</Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Additional Properties */}
        {properties && Object.keys(properties).length > 0 && (
          <div className="space-y-2">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-kaycoo-teal-dark">
              <Database className="h-4 w-4" />
              Properties
            </h3>
            <div className="space-y-1 text-sm">
              {Object.entries(properties)
                .filter(([key]) => !key.startsWith('d365_') && key !== 'created_at')
                .map(([key, value]) => (
                  <div key={key} className="flex justify-between text-kaycoo-charcoal-2">
                    <span className="font-medium">{key.replace(/_/g, ' ')}:</span>
                    <span className="text-right">
                      {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* View Full Entity Link */}
        <div className="pt-4">
          <Button
            variant="outline"
            className="w-full border-kaycoo-teal text-kaycoo-teal-dark hover:bg-kaycoo-teal hover:text-white"
            onClick={() => {
              // Navigate to the entity detail page
              const path = {
                issue: `/issues/${node.entity_id}`,
                knowledge_entry: '/knowledge',
                resolution_method: '/knowledge',
              }[node.entity_type]

              if (path) {
                window.location.href = path
              }
            }}
          >
            <ExternalLink className="mr-2 h-4 w-4" />
            View Full Details
          </Button>
        </div>
      </div>
    </div>
  )
}
